/*************************
 * Author : Matthieu ROBIN
 *************************/
import React, { FunctionComponent, useState } from "react";
import moment from "moment";
import { Car, CarPosition, CarStatus } from "../models/car";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardMedia,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Divider,
  FormControlLabel,
  FormLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  Zoom,
  Grid2 as Grid,
  Table,
  TableRow,
  TableCell,
  TableContainer,
  TableBody,
  Badge,
} from "@mui/material";
import {
  Archive,
  Backspace,
  Cancel,
  Check,
  Close,
  Edit,
  EmojiEvents,
  Euro,
  Factory,
  Grade,
  LightMode,
  LockClock,
  OpenInNew,
  Person,
  Savings,
  Sell,
  SportsMotorsports,
  Store,
  TextSnippet,
  WorkspacePremium,
} from "@mui/icons-material";
import { Carousel } from "./Carousel";
import { CarService } from "../services/car-service";
import { useNavigate } from "react-router";
import { useAppDispatch } from "../app/hooks";
import { updateCar } from "../slices/cars-slice";
import { ErrorResponse } from "../models/response";

import placeholder from "../images/car_placeholder.png";
import { makeStyles } from "@mui/styles";
import { LoadingButton } from "@mui/lab";
import ebayLogo from "../images/ebay.svg";

type CarDetailsProps = {
  car: Car;
};

const getChipColor = (status: CarStatus) => {
  switch (status) {
    case "Nouveau":
      return "#4DD091";
    case "Notable":
      return "#5f6f52";
    case "Souhaité":
      return "#B980FF";
    case "Réservé":
      return "#E6C84F";
    case "A garder":
      return "#00A5E3";
    case "A vendre":
      return "#FFA23A";
    case "En vente":
      return "#FF6F68";
    case "Vendu":
      return "#74737A";
  }
};

const getPositionIcon = (position: CarPosition) => {
  switch (position) {
    case "1st":
      return <EmojiEvents htmlColor="#FFD700" />;
    case "2nd":
      return <EmojiEvents htmlColor="#BCC6CC" />;
    case "3rd":
      return <EmojiEvents htmlColor="#CD7F32" />;
  }
};

const useStyles = makeStyles((theme) => ({
  detail: {
    display: "flex",
    alignItems: "top",
  },
  label: {
    width: 120,
  },
}));

const CarDetails: FunctionComponent<CarDetailsProps> = ({ car }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const classes = useStyles();
  const logoName = car.brand.toLocaleLowerCase().replace(" ", "-");

  const title = [
    car.brand,
    car.type,
    car.number ? `#${car.number}` : undefined,
  ].join(" ");

  const subheader = [car.competition, car.year].join(" ");

  const gain: number | undefined =
    car.purchasePrice && car.status === "Vendu"
      ? car.sell.trueSellPrice +
        car.sell.shippingCosts -
        car.sell.trueShippingCosts -
        car.purchasePrice -
        car.sell.fees
      : undefined;

  const [updateStatus, setUpdateStatus] = useState<{
    inProgress: boolean;
    errors: string[];
  }>({ inProgress: false, errors: null });

  const [openSellDialog, setOpenSellDialog] = useState<boolean>(false);
  const [sellStatus, setSellStatus] = useState<{
    inProgress: boolean;
    errors: string[];
  }>({ inProgress: false, errors: null });

  const [openRemoveSellConfirm, setOpenRemoveSellConfirm] =
    useState<boolean>(false);
  const [removeSellStatus, setRemoveSellStatus] = useState<{
    inProgress: boolean;
    errors: string[];
  }>({ inProgress: false, errors: null });

  const [openSelledDialog, setOpenSelledDialog] = useState<boolean>(false);
  const [selledStatus, setSelledStatus] = useState<{
    inProgress: boolean;
    errors: string[];
  }>({ inProgress: false, errors: null });

  const [openResetConfirm, setOpenResetConfirm] = useState<boolean>(false);
  const [resetStatus, setResetStatus] = useState<{
    inProgress: boolean;
    errors: string[];
  }>({ inProgress: false, errors: null });

  const goToCars = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const queryParams = searchParams.toString()
      ? `?${searchParams.toString()}`
      : "";

    navigate(`/cars${queryParams}`);
  };

  const editCar = (id: string) => {
    const searchParams = new URLSearchParams(window.location.search);
    const queryParams = searchParams.toString()
      ? `?${searchParams.toString()}`
      : "";

    navigate(`/cars/${id}/edit${queryParams}`);
  };

  const handleStatusUpdate = async (status: CarStatus) => {
    try {
      setUpdateStatus({ inProgress: true, errors: null });
      const updatedCar = { ...car, status };
      await CarService.updateCar(car._id, updatedCar).then((car) => {
        dispatch(updateCar(car));
        setUpdateStatus({ inProgress: false, errors: null });
      });
    } catch (e: any) {
      const errorResponse = e as ErrorResponse;
      setUpdateStatus({
        inProgress: false,
        errors: errorResponse.errors.map((err) => err.msg),
      });
    }
  };

  const handleSellCar = async (sellType: string, price: number) => {
    try {
      setSellStatus({ inProgress: true, errors: null });
      await CarService.sellCar(car._id, car.updatedAt, sellType, price).then(
        (car) => {
          if (sellType === "ebay") {
            window.open(
              `https://www.ebay.fr/sl/list?mode=ReviseItem&itemId=${car.sell.id}`,
            );
          }
          dispatch(updateCar(car));
          setOpenSellDialog(false);
          setSellStatus({ inProgress: false, errors: null });
        },
      );
    } catch (e: any) {
      const errorResponse = e as ErrorResponse;
      setSellStatus({
        inProgress: false,
        errors: errorResponse.errors.map((err) => err.msg),
      });
    }
  };

  const handleCancelSellCar = () => {
    setOpenSellDialog(false);
    setSellStatus({
      inProgress: false,
      errors: null,
    });
  };

  const handleRemoveSellCar = async () => {
    try {
      setRemoveSellStatus({
        inProgress: true,
        errors: null,
      });
      await CarService.cancelSale(car._id, car.updatedAt).then((car) => {
        dispatch(updateCar(car));
        setOpenRemoveSellConfirm(false);
        setRemoveSellStatus({
          inProgress: false,
          errors: null,
        });
      });
    } catch (e: any) {
      const errorResponse = e as ErrorResponse;
      setRemoveSellStatus({
        inProgress: false,
        errors: errorResponse.errors.map((err) => err.msg),
      });
    }
  };

  const handleCancelRemoveSellCar = () => {
    setOpenRemoveSellConfirm(false);
    setRemoveSellStatus({
      inProgress: false,
      errors: null,
    });
  };

  const handleSelledCar = async (
    trueSellPrice: number,
    shippingCosts: number,
    trueShippingCosts: number,
    fees: number,
  ) => {
    try {
      setSelledStatus({
        inProgress: true,
        errors: null,
      });
      const updatedCar: Car = {
        ...car,
        status: "Vendu",
        sell: {
          ...car.sell,
          trueSellPrice,
          shippingCosts,
          trueShippingCosts,
          trueSellDate: new Date(),
          fees,
        },
      };
      await CarService.updateCar(car._id, updatedCar).then((car) => {
        dispatch(updateCar(car));
        setOpenSelledDialog(false);
        setSelledStatus({
          inProgress: false,
          errors: null,
        });
      });
    } catch (e: any) {
      const errorResponse = e as ErrorResponse;
      setSelledStatus({
        inProgress: false,
        errors: errorResponse.errors.map((err) => err.msg),
      });
    }
  };

  const handleCancelSelledCar = () => {
    setOpenSelledDialog(false);
    setSelledStatus({ inProgress: false, errors: null });
  };

  const handleResetCar = async () => {
    try {
      setResetStatus({ inProgress: true, errors: null });
      const updatedCar: Car = { ...car, status: "Nouveau", sell: null };
      await CarService.updateCar(car._id, updatedCar).then((car) => {
        dispatch(updateCar(car));
        setOpenResetConfirm(false);
        setResetStatus({ inProgress: false, errors: null });
      });
    } catch (e: any) {
      const errorResponse = e as ErrorResponse;
      setResetStatus({
        inProgress: false,
        errors: errorResponse.errors.map((err) => err.msg),
      });
    }
  };

  const handleCancelResetCar = () => {
    setOpenResetConfirm(false);
    setResetStatus({ inProgress: false, errors: null });
  };

  const showSimilarObjects = () => {
    const searchParams = [
      car.brand,
      car.type,
      car.number,
      car.competition,
      car.year,
    ];
    const searchQuery = searchParams
      .filter((param) => param !== undefined)
      .join("+");
    window.open(
      `https://www.ebay.fr/sch/i.html?_nkw=${searchQuery}&_sacat=220&LH_Complete=1&LH_PrefLoc=2&LH_Sold=1`,
    );
  };

  const showSellAd = () => {
    window.open(`https://www.ebay.fr/itm/${car.sell.id}`);
  };

  return (
    <Card
      style={{ minHeight: "100%", display: "flex", flexDirection: "column" }}
    >
      <CardHeader
        avatar={
          <img
            src={`/logos/${logoName}.svg`}
            height={30}
            width={50}
            onError={({ currentTarget }) => {
              currentTarget.onerror = null;
              currentTarget.src = "/logos/unknown.svg";
            }}
          />
        }
        title={title}
        subheader={subheader}
        action={
          <Stack direction="row" alignItems="center">
            {car.category && (
              <Chip
                label={car.category}
                variant="outlined"
                size="small"
                icon={car.categoryWinner && <WorkspacePremium />}
                color="secondary"
                style={{
                  borderRadius: 5,
                  marginRight: 5,
                }}
              />
            )}
            {getPositionIcon(car.position)}
            <IconButton aria-label="settings" onClick={() => goToCars()}>
              <Close />
            </IconButton>
          </Stack>
        }
      />
      <CardMedia>
        <Carousel images={car.photos} placeholder={placeholder} />
      </CardMedia>
      <CardContent>
        <Stack direction="row" spacing={1} marginBottom={2}>
          <Chip
            label={car.status}
            style={{
              borderRadius: 5,
              backgroundColor: getChipColor(car.status),
              color: "white",
            }}
          />
          <Divider orientation="vertical" variant="middle" flexItem />
          {car.status === "Nouveau" ? (
            <>
              <Button
                onClick={() => handleStatusUpdate("Notable")}
                startIcon={<LightMode />}
                variant="outlined"
                size="small"
                disabled={updateStatus.inProgress}
              >
                Notable
              </Button>
              <Button
                onClick={() => handleStatusUpdate("A vendre")}
                endIcon={<Store />}
                variant="outlined"
                size="small"
                disabled={updateStatus.inProgress}
              >
                A vendre
              </Button>
            </>
          ) : car.status === "Notable" ? (
            <>
              <Button
                onClick={() => handleStatusUpdate("Souhaité")}
                startIcon={<Grade />}
                variant="outlined"
                size="small"
                disabled={updateStatus.inProgress}
              >
                Souhaité
              </Button>
            </>
          ) : car.status === "Souhaité" ? (
            <>
              <Button
                onClick={() => handleStatusUpdate("Notable")}
                startIcon={<LightMode />}
                variant="outlined"
                size="small"
                disabled={updateStatus.inProgress}
              >
                Notable
              </Button>
              <Button
                onClick={() => handleStatusUpdate("Réservé")}
                endIcon={<LockClock />}
                variant="outlined"
                size="small"
                disabled={updateStatus.inProgress}
              >
                Réservé
              </Button>
            </>
          ) : car.status === "Réservé" ? (
            <>
              <Button
                onClick={() => handleStatusUpdate("Souhaité")}
                startIcon={<Grade />}
                variant="outlined"
                size="small"
                disabled={updateStatus.inProgress}
              >
                Souhaité
              </Button>
              <Button
                onClick={() => handleStatusUpdate("A garder")}
                endIcon={<Archive />}
                variant="outlined"
                size="small"
                disabled={updateStatus.inProgress}
              >
                A garder
              </Button>
            </>
          ) : car.status === "A garder" ? (
            <>
              <Button
                onClick={() => handleStatusUpdate("Souhaité")}
                startIcon={<Grade />}
                variant="outlined"
                size="small"
                disabled={updateStatus.inProgress}
              >
                Souhaité
              </Button>
              <Button
                onClick={() => handleStatusUpdate("A vendre")}
                endIcon={<Store />}
                variant="outlined"
                size="small"
                disabled={updateStatus.inProgress}
              >
                A vendre
              </Button>
            </>
          ) : car.status === "A vendre" ? (
            <>
              <Button
                onClick={() => setOpenSellDialog(true)}
                startIcon={<Sell />}
                variant="outlined"
                size="small"
                disabled={updateStatus.inProgress}
              >
                Vendre
              </Button>
              <Dialog
                open={openSellDialog}
                onClose={handleCancelSellCar}
                PaperProps={{
                  component: "form",
                  onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
                    event.preventDefault();
                    const formData = new FormData(event.currentTarget);
                    const formJson = Object.fromEntries(
                      (formData as any).entries(),
                    );
                    const sellType = formJson.sellType;
                    const price = formJson.price;
                    handleSellCar(sellType, price);
                  },
                }}
              >
                <DialogContent>
                  <FormLabel>Type de vente</FormLabel>
                  <Stack spacing={1}>
                    <RadioGroup row defaultValue="ebay" name="sellType">
                      <FormControlLabel
                        value="ebay"
                        control={<Radio />}
                        label="Ebay"
                      />
                      <FormControlLabel
                        value="free"
                        control={<Radio />}
                        label="Libre"
                      />
                    </RadioGroup>
                    <Stack direction="row" spacing={1}>
                      <TextField label="Prix de vente" name="price" />
                      <Button
                        startIcon={<OpenInNew />}
                        size="small"
                        onClick={showSimilarObjects}
                      >
                        Voir les objets similaires
                      </Button>
                    </Stack>
                    <Typography>Prix d'achat: {car.purchasePrice} €</Typography>
                  </Stack>
                  {sellStatus.errors && (
                    <Zoom in={sellStatus.errors.length > 0}>
                      <Alert severity="error" style={{ marginTop: 10 }}>
                        <AlertTitle>Mise en vente impossible</AlertTitle>
                        {sellStatus.errors.map((error) => (
                          <li key={error}>{error}</li>
                        ))}
                      </Alert>
                    </Zoom>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleCancelSellCar}>Annuler</Button>
                  <LoadingButton
                    type="submit"
                    autoFocus
                    startIcon={<Check />}
                    loading={sellStatus.inProgress}
                    loadingPosition="start"
                    variant="contained"
                  >
                    Confirmer
                  </LoadingButton>
                </DialogActions>
              </Dialog>
              <Button
                onClick={() => handleStatusUpdate("A garder")}
                startIcon={<Archive />}
                variant="outlined"
                size="small"
                disabled={updateStatus.inProgress}
              >
                A garder
              </Button>
            </>
          ) : car.status === "En vente" ? (
            <>
              <Button
                onClick={() => setOpenSelledDialog(true)}
                endIcon={<Savings />}
                variant="outlined"
                size="small"
                disabled={updateStatus.inProgress || car.sell?.type === "ebay"}
              >
                Vendu
              </Button>
              <Dialog
                open={openSelledDialog}
                onClose={handleCancelSelledCar}
                PaperProps={{
                  component: "form",
                  onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
                    event.preventDefault();
                    const formData = new FormData(event.currentTarget);
                    const formJson = Object.fromEntries(
                      (formData as any).entries(),
                    );
                    const trueSellPrice = formJson.trueSellPrice;
                    const shippingCosts = formJson.shippingCosts;
                    const trueShippingCosts = formJson.trueShippingCosts;
                    const fees = formJson.fees;
                    handleSelledCar(
                      trueSellPrice,
                      shippingCosts,
                      trueShippingCosts,
                      fees,
                    );
                  },
                }}
              >
                <DialogContent>
                  <Stack spacing={1}>
                    <TextField label="Prix de vente" name="trueSellPrice" />
                    <TextField label="Frais de port" name="shippingCosts" />
                    <TextField label="FDP réels" name="trueShippingCosts" />
                    <TextField label="Commission" name="fees" />
                  </Stack>
                  {selledStatus.errors && (
                    <Zoom in={selledStatus.errors.length > 0}>
                      <Alert severity="error" style={{ marginTop: 10 }}>
                        <AlertTitle>
                          Confirmation de vente impossible
                        </AlertTitle>
                        {selledStatus.errors.map((error) => (
                          <li key={error}>{error}</li>
                        ))}
                      </Alert>
                    </Zoom>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleCancelSelledCar}>Annuler</Button>
                  <LoadingButton
                    type="submit"
                    autoFocus
                    startIcon={<Check />}
                    loading={selledStatus.inProgress}
                    loadingPosition="start"
                    variant="contained"
                  >
                    Confirmer
                  </LoadingButton>
                </DialogActions>
              </Dialog>
              <Button
                onClick={() => setOpenRemoveSellConfirm(true)}
                startIcon={<Cancel />}
                variant="outlined"
                size="small"
                disabled={updateStatus.inProgress}
              >
                Annuler
              </Button>
              <Dialog
                open={openRemoveSellConfirm}
                onClose={handleCancelRemoveSellCar}
              >
                <DialogContent>
                  <DialogContentText>
                    Êtes-vous sûr de vouloir annuler la vente ?
                  </DialogContentText>
                  {removeSellStatus.errors && (
                    <Zoom in={removeSellStatus.errors.length > 0}>
                      <Alert severity="error" style={{ marginTop: 10 }}>
                        <AlertTitle>Impossible d'annuler la vente</AlertTitle>
                        {removeSellStatus.errors.map((error) => (
                          <li key={error}>{error}</li>
                        ))}
                      </Alert>
                    </Zoom>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleCancelRemoveSellCar}>Annuler</Button>
                  <LoadingButton
                    onClick={handleRemoveSellCar}
                    autoFocus
                    startIcon={<Cancel />}
                    loading={removeSellStatus.inProgress}
                    loadingPosition="start"
                    variant="contained"
                  >
                    Confirmer
                  </LoadingButton>
                </DialogActions>
              </Dialog>
            </>
          ) : (
            car.status === "Vendu" && (
              <>
                <Button
                  onClick={() => setOpenResetConfirm(true)}
                  endIcon={<Backspace />}
                  variant="outlined"
                  size="small"
                  disabled={updateStatus.inProgress}
                >
                  Nouveau
                </Button>
                <Dialog open={openResetConfirm} onClose={handleCancelResetCar}>
                  <DialogContent>
                    <DialogContentText>
                      Êtes-vous sûr de vouloir réinitialiser ce véhicule ?
                    </DialogContentText>
                    {resetStatus.errors && (
                      <Zoom in={resetStatus.errors.length > 0}>
                        <Alert severity="error" style={{ marginTop: 10 }}>
                          <AlertTitle>Réinitialisation impossible</AlertTitle>
                          {resetStatus.errors.map((error) => (
                            <li key={error}>{error}</li>
                          ))}
                        </Alert>
                      </Zoom>
                    )}
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={handleCancelResetCar}>Annuler</Button>
                    <LoadingButton
                      onClick={handleResetCar}
                      autoFocus
                      startIcon={<Check />}
                      loading={resetStatus.inProgress}
                      loadingPosition="start"
                      variant="contained"
                    >
                      Confirmer
                    </LoadingButton>
                  </DialogActions>
                </Dialog>
              </>
            )
          )}
        </Stack>
        {updateStatus.errors && (
          <Zoom in={updateStatus.errors.length > 0}>
            <Alert severity="error" style={{ marginTop: 10 }}>
              <AlertTitle>Impossible de modifier le status</AlertTitle>
              {updateStatus.errors.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </Alert>
          </Zoom>
        )}
        <Divider style={{ marginTop: "10px", marginBottom: "10px" }} />
        <Stack direction={"row"} spacing={1}>
          <SportsMotorsports />
          <Box className={classes.detail}>
            <Box>
              <Typography className={classes.label}>Pilotes:</Typography>
            </Box>
            <Stack
              direction="row"
              style={{ gap: 5, flexWrap: "wrap", maxWidth: "100%" }}
            >
              {car.pilots.length > 0
                ? car.pilots.map((pilot, idx) => (
                    <Chip
                      key={`${idx}_${pilot}`}
                      label={pilot}
                      variant="outlined"
                      size="small"
                    />
                  ))
                : "-"}
            </Stack>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Factory />
          <Box display={"flex"} alignContent="center" alignItems="center">
            <Typography className={classes.label}>Fabricant:</Typography>
            <Typography>{car.manufacturer || "-"}</Typography>
            <Typography style={{ marginLeft: 10 }} variant="caption">
              {car.reference || (car.manufacturer && "-")}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Euro />
          <Box className={classes.detail}>
            <Typography className={classes.label}>Prix d'achat:</Typography>
            {car.purchasePrice || "-"} €
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Person />
          <Box className={classes.detail}>
            <Typography className={classes.label}>Propriétaire:</Typography>
            {car.owner || "-"}
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <TextSnippet />
          <Box className={classes.detail}>
            <Typography className={classes.label}>Détails:</Typography>
            <Typography component="pre">{car.details || "-"}</Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Archive />
          <Box className={classes.detail}>
            <Typography className={classes.label}>Emplacement:</Typography>
            {car.storageLocation || "-"}
          </Box>
        </Stack>
        {car.sell && (
          <>
            <Paper
              style={{
                padding: 5,
                marginTop: 10,
                backgroundColor: "#FAFAFA",
              }}
              elevation={2}
              variant="outlined"
              square={false}
            >
              <Grid container>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2">
                    {car.sell.type === "ebay" && (
                      <img
                        src={ebayLogo}
                        height={14}
                        style={{ marginRight: 5 }}
                      />
                    )}
                    {car.status === "En vente"
                      ? "Vente en cours"
                      : `Vente terminée le ${moment(
                          car.sell.trueSellDate,
                        ).format("DD/MM/YYYY à HH[h]mm")} (mise à prix: ${
                          car.sell.sellPrice
                        } €)`}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  {car.sell.type === "ebay" && (
                    <Button
                      startIcon={<OpenInNew />}
                      size="small"
                      onClick={showSellAd}
                    >
                      Accéder à l'annonce
                    </Button>
                  )}
                </Grid>
              </Grid>
              {car.status === "En vente" && (
                <Typography variant="body2">
                  Mise à prix: {car.sell.sellPrice} €
                </Typography>
              )}
              {car.status === "Vendu" && (
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell>Prix de vente</TableCell>
                        <TableCell>FDP</TableCell>
                        <TableCell>FDP réels</TableCell>
                        <TableCell>Commission</TableCell>
                        <TableCell>+/- value</TableCell>
                      </TableRow>
                    </TableBody>
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          {car.sell.trueSellPrice != undefined
                            ? car.sell.trueSellPrice.toFixed(2)
                            : "-"}{" "}
                          €
                        </TableCell>
                        <TableCell>
                          {car.sell.shippingCosts != undefined
                            ? car.sell.shippingCosts.toFixed(2)
                            : "-"}{" "}
                          €
                        </TableCell>
                        <TableCell>
                          {car.sell.trueShippingCosts != undefined
                            ? car.sell.trueShippingCosts.toFixed(2)
                            : "-"}{" "}
                          €
                        </TableCell>
                        <TableCell>
                          {car.sell.fees != undefined
                            ? car.sell.fees.toFixed(2)
                            : "-"}{" "}
                          €
                        </TableCell>
                        <TableCell
                          style={{
                            color:
                              gain != undefined
                                ? gain >= 0
                                  ? "green"
                                  : "red"
                                : "inherit",
                          }}
                        >
                          {gain != undefined ? gain.toFixed(2) : "-"} €
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </>
        )}
        <Divider style={{ marginTop: 10 }} />
        {car.updatedAt && (
          <Typography color="textSecondary" variant="body2">
            Dernière modification le{" "}
            {moment(car.updatedAt).format("DD/MM/YYYY à HH[h]mm")}
          </Typography>
        )}
      </CardContent>
      <Box flexDirection="column" flexGrow={1} />
      <CardActions>
        <Button onClick={() => goToCars()}>Retour</Button>
        <Button
          onClick={() => editCar(car._id)}
          startIcon={<Edit />}
          style={{ marginLeft: "10px" }}
          variant="contained"
        >
          Editer
        </Button>
      </CardActions>
    </Card>
  );
};

export default CarDetails;
